import type {
  Booking,
  BookingFlag,
  CustomerRecord,
  Deposit,
  SalePayment,
  InvoiceStatus,
  RefsLocation,
  RescheduleReason,
} from '@/domain/types';
import { formatBodyPart } from '@/utils/format';

/**
 * Display labels for reschedule reasons. Shared by the booking-detail history
 * list and the calendar reschedule-reason picker so both read identically.
 */
export const RESCHEDULE_REASON_LABEL: Record<RescheduleReason, string> = {
  'gap-fill': 'Gap fill',
  'artist-move': 'Artist move',
  'client-move': 'Client move',
  'cancellation-fill': 'Cancellation fill',
  other: 'Other',
};

/**
 * Deposit paid, derived from the ledger per the studio's real accounting:
 * paid + topup + rollover-in − rollover-out − forfeit. Falls back to the flat
 * `amountPaid` when no ledger is present so older seeds still show a figure.
 */
export function depositPaidAud(deposit: Deposit | undefined): number {
  if (!deposit) return 0;
  if (deposit.ledger && deposit.ledger.length > 0) {
    return deposit.ledger.reduce((sum, e) => {
      switch (e.kind) {
        case 'paid':
        case 'topup':
        case 'rollover-in':
        case 'reinstate': // management reversal of a forfeit — money is back
          return sum + e.amountAud;
        case 'rollover-out':
        case 'forfeit':
          return sum - e.amountAud;
        default:
          return sum; // refunded / transfer-in / transfer-out excluded from "paid"
      }
    }, 0);
  }
  return deposit.amountPaid ?? (deposit.status === 'paid' ? deposit.amountAud : 0);
}

/** The placeholder a held booking gets from a deposit link before any brief exists. */
const PLACEHOLDER_BRIEF = /^held on deposit link\.?$/i;

const titleWord = (tag: string): string => {
  const words = tag.replace(/-/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** How a pay-at-studio booking stands: still to pay at the desk, or paid there. */
export type PayAtStudioState = 'due' | 'paid';

/**
 * A hold's free-text brief as screens print it (tv-958x): the placeholder every
 * hold is made with ("Held on deposit link.") names how the client pays when the
 * booking is a pay-at-studio one, where no link was ever sent:
 * "Held to pay at the studio.", then "Deposit paid at the studio." once the desk
 * takes it. A real brief is never rewritten. Screens pass
 * `payAtStudioStateFor(...)` (domain/depositStatus) as `state`.
 */
export function holdBriefText(
  designBrief: string | undefined,
  state: PayAtStudioState | undefined,
  /** The booking's status: once the client has arrived the hold isn't "held" any more (calendar-11 c). */
  status?: string,
): string {
  const brief = designBrief ?? '';
  if (!state || !PLACEHOLDER_BRIEF.test(brief.trim())) return brief;
  if (state === 'paid') return 'Deposit paid at the studio.';
  return status && ARRIVED.has(status) ? 'Arrived · deposit to pay at the desk.' : 'Held to pay at the studio.';
}

const ARRIVED: ReadonlySet<string> = new Set(['checked-in', 'in-chair', 'completed']);

/**
 * ONE line describing a booking's brief (tv-2na.11.23, D5), for the Floor: the
 * rule-built one-line summary when present, else the structured brief — styles, colour,
 * placement ("Blackwork · black & grey · Forearm") — else a real free-text
 * brief. Never the customer's stale designIntent, never the deposit-link
 * placeholder ("Held on deposit link."): an empty string means no brief yet.
 */
export function bookingBriefLine(
  b: Pick<
    Booking,
    'designBrief' | 'summary' | 'styleTags' | 'colour' | 'bodyPart' | 'bodyPartSide' | 'bodyPartQualifier' | 'placement'
  >,
): string {
  if (b.summary?.trim()) return b.summary.trim();
  const parts: string[] = [];
  if (b.styleTags?.length) parts.push(b.styleTags.map(titleWord).join(' + '));
  if (b.colour) parts.push(b.colour === 'black-and-grey' ? 'black & grey' : 'colour');
  const where = formatBodyPart(b);
  if (where) parts.push(where);
  if (parts.length > 0) return parts.join(' · ');
  const free = (b.designBrief ?? '').trim();
  return PLACEHOLDER_BRIEF.test(free) ? '' : free;
}

/** The money picture of ONE booking (tv-2na.8.21). */
export interface BookingMoney {
  dayRateAud: number | null;
  /** Money that covers this session: the deposit on it, or once checked out, the sale. */
  paidAud: number;
  /** Day rate still owed, never negative; null with no day rate. */
  balanceAud: number | null;
  /** A checkout sale was recorded for this booking. */
  settled: boolean;
}

/**
 * The ONE balance rule for a booking (tv-2na.8.21, D7). Before checkout the
 * deposit paid (ledger-derived) covers part of the day rate. Once a checkout
 * sale exists it is the whole truth for the session: what was charged
 * (`balanceAud`, after any adjustment) plus the deposit applied to it. The
 * deposit is NOT added on top — it was either applied (inside the sale) or
 * rolled forward to the next booking (not this session's money) — so a done,
 * fully paid booking reads BAL $0.
 */
export function bookingMoney(
  booking: Pick<Booking, 'id' | 'dayRateAud'>,
  deposit: Deposit | undefined,
  salePayments: ReadonlyArray<SalePayment>,
): BookingMoney {
  const sales = salePayments.filter((s) => s.bookingId === booking.id);
  const settled = sales.length > 0;
  const paidAud = settled
    ? sales.reduce((sum, s) => sum + s.balanceAud + s.depositAppliedAud, 0)
    : depositPaidAud(deposit);
  const dayRateAud = booking.dayRateAud ?? null;
  const balanceAud = dayRateAud == null ? null : Math.max(0, dayRateAud - paidAud);
  return { dayRateAud, paidAud, balanceAud, settled };
}

/**
 * Lifetime spend (tv-2na.8.21): what the customer has actually paid at checkout,
 * derived from the recorded sales on their bookings (session money charged +
 * deposit applied + merch). Never reads lower than a seeded lifetime figure
 * (historic spend from before the app has no sale records).
 */
export function lifetimeSpendAud(
  customer: Pick<CustomerRecord, 'id' | 'lifetimeSpendAud'>,
  bookings: Readonly<Record<string, Pick<Booking, 'customerId'>>>,
  salePayments: ReadonlyArray<SalePayment>,
): number {
  const derived = salePayments.reduce(
    (sum, s) =>
      bookings[s.bookingId]?.customerId === customer.id
        ? sum + s.balanceAud + s.depositAppliedAud + s.merchAud
        : sum,
    0,
  );
  return Math.max(customer.lifetimeSpendAud ?? 0, derived);
}

/**
 * A deposit used up on piece completion (Diagram 02, tv-b5g): checkout wrote its
 * explicit `redeemed` movement. The status stays 'paid' (money came in), but it
 * is no longer money on file waiting for a date (tv-2na.4).
 */
export function isDepositRedeemed(deposit: Deposit): boolean {
  return (deposit.ledger ?? []).some((e) => e.kind === 'redeemed');
}

// Flag → label + tone. Colours follow the Vault lead-colour system mapped onto
// muted variants that read on the dark ink canvas. Full literal classes so
// Tailwind's JIT keeps them.
export const FLAG_STYLE: Record<BookingFlag, { label: string; cls: string }> = {
  'medical-note': { label: 'Medical note', cls: 'border-blood/50 bg-blood/15 text-blood' },
  'deposit-topup-pending': {
    label: 'Top-up pending',
    cls: 'border-gold-bright/50 bg-gold/15 text-gold-bright',
  },
  'late-fill': { label: 'Late fill', cls: 'border-azure/40 bg-azure/15 text-azure' },
};

export const REFS_LOCATION_LABEL: Record<RefsLocation, string> = {
  uploaded: 'Uploaded',
  'on-client-card': 'On client card',
  'with-artist': 'With artist',
  'ig-dms': 'IG DMs',
  'physical-folder': 'Physical folder',
};

export const INVOICE_LABEL: Record<InvoiceStatus, string> = {
  'not-issued': 'Not invoiced',
  draft: 'Invoice draft',
  issued: 'Invoice issued',
  paid: 'Invoice paid',
  'uninvoiced-writeoff': 'Write-off',
};

/**
 * Time in the chair (Q13, DECIDED 25 Sep): REAL time since the client was seated.
 * The seat is stamped on the sim clock, which stands still between facilitator
 * steps, so elapsed = sim time since the seat (a day step while in the chair
 * still counts) + wall time since the seat moment (`wallAnchorMs`). Pure.
 */
export function inChairElapsedMs(args: {
  simNowIso: string;
  seatedAtIso: string | undefined;
  wallAnchorMs: number;
  wallNowMs: number;
}): number {
  if (!args.seatedAtIso) return 0;
  const sim = new Date(args.simNowIso).getTime() - new Date(args.seatedAtIso).getTime();
  if (sim < 0) return 0;
  return sim + Math.max(0, args.wallNowMs - args.wallAnchorMs);
}

/** localStorage key: bookingId -> { seat, wallMs } — the wall moment each seat began. */
export const SEAT_WALL_KEY = 'ati-seat-wall';

/**
 * The wall-clock moment this browser first saw booking `bookingId` seated at
 * `seatKey` (its seatedAt stamp). Kept in localStorage so a reload keeps
 * ticking from the seat, not from the reload; a new seat re-anchors. Storage
 * failures fall back to `nowMs` (the timer then runs from this view).
 */
export function seatWallAnchor(bookingId: string, seatKey: string, nowMs: number): number {
  try {
    const all = JSON.parse(localStorage.getItem(SEAT_WALL_KEY) ?? '{}') as Record<string, { seat: string; wallMs: number }>;
    const hit = all[bookingId];
    if (hit && hit.seat === seatKey && Number.isFinite(hit.wallMs)) return hit.wallMs;
    all[bookingId] = { seat: seatKey, wallMs: nowMs };
    localStorage.setItem(SEAT_WALL_KEY, JSON.stringify(all));
  } catch {
    // storage unavailable — anchor on this view.
  }
  return nowMs;
}
